import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { C, Badge } from "@coremonit/design-system";
import { ChevronLeft, ChevronRight, ChevronDown, PlusCircle, Sparkles, LogOut } from "lucide-react";
import { GROUP_LABEL, MICROSERVICES_NAV } from "./nav";
import { useAuth } from "../auth/AuthContext";
import { InfrastructureExplorer } from "./InfrastructureExplorer";

export function Sidebar({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const grouped = useMemo(() => {
    const g: Record<string, typeof MICROSERVICES_NAV> = {};
    for (const item of MICROSERVICES_NAV) {
      (g[item.group] ??= []).push(item);
    }
    return g;
  }, []);
  const nav = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [projectsOpen, setProjectsOpen] = useState(false);

  const projectsActive = location.pathname.startsWith("/projects") || location.pathname.startsWith("/clusters");

  return (
    <aside
      style={{
        width: collapsed ? 62 : 232,
        background: C.bg,
        borderRight: `1px solid ${C.border}`,
        display: "flex", flexDirection: "column",
        transition: "width 0.2s ease",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      {/* Logo */}
      <div style={{ padding: 14, display: "flex", alignItems: "center", gap: 10, borderBottom: `1px solid ${C.border}` }}>
        <div
          style={{
            width: 32, height: 32, borderRadius: 10,
            background: `linear-gradient(135deg, ${C.blue}, ${C.cyan})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 0 16px rgba(45,127,249,0.4)`,
            flexShrink: 0,
          }}
        >
          <Sparkles size={16} color="#fff" />
        </div>
        {!collapsed && (
          <div style={{ minWidth: 0 }}>
            <div style={{ color: C.text, fontSize: 13, fontWeight: 700, whiteSpace: "nowrap" }}>CoreMonit</div>
            <div style={{ color: C.textMuted, fontSize: 9, letterSpacing: 0.6, textTransform: "uppercase", whiteSpace: "nowrap" }}>Microservices</div>
          </div>
        )}
      </div>

      {/* Add service CTA */}
      <div style={{ padding: 10 }}>
        <button
          onClick={() => nav("/services/new")}
          style={{
            width: "100%", display: "flex", alignItems: "center", gap: 8,
            padding: collapsed ? 8 : "9px 12px",
            background: `linear-gradient(90deg, ${C.blue}, #4a92ff)`,
            color: "#fff",
            border: "none", borderRadius: 12,
            boxShadow: `0 6px 20px rgba(45,127,249,0.4)`,
            fontSize: 11, fontWeight: 700, cursor: "pointer",
            justifyContent: "center",
          }}
        >
          <PlusCircle size={13} />
          {!collapsed && <span>Add Microservice</span>}
        </button>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, overflowY: "auto", padding: "4px 10px", display: "flex", flexDirection: "column", gap: 12 }}>
        {(["monitor", "explore", "investigate", "operate"] as const).map((group) => (
          <div key={group}>
            {!collapsed && (
              <div style={{ color: C.textMuted, fontSize: 9, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", padding: "6px 8px 4px" }}>
                {GROUP_LABEL[group]}
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {grouped[group]?.map((item) => {
                const Icon = item.icon;

                if (item.id === "projects") {
                  return (
                    <div key={item.id}>
                      <div
                        style={{
                          display: "flex", alignItems: "center", gap: 4,
                          borderRadius: 10,
                          background: projectsActive ? C.blueDim : "transparent",
                          border: `1px solid ${projectsActive ? C.borderBright : "transparent"}`,
                        }}
                      >
                        <NavLink
                          to={item.path}
                          end={false}
                          style={{
                            flex: 1, display: "flex", alignItems: "center", gap: 10,
                            padding: collapsed ? "8px 0" : "7px 10px",
                            justifyContent: collapsed ? "center" : "flex-start",
                            color: projectsActive ? C.blue : C.textSub,
                            textDecoration: "none",
                            fontSize: 11, fontWeight: 500,
                          }}
                          title={collapsed ? item.label : undefined}
                        >
                          <Icon width={14} height={14} style={{ flexShrink: 0 }} />
                          {!collapsed && (
                            <>
                              <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.label}</span>
                              {item.badge && <Badge color={item.badgeColor}>{item.badge}</Badge>}
                            </>
                          )}
                        </NavLink>
                        {!collapsed && (
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setProjectsOpen((v) => !v); }}
                            aria-expanded={projectsOpen}
                            aria-label={projectsOpen ? "Collapse project tree" : "Expand project tree"}
                            title={projectsOpen ? "Hide project tree" : "Show project tree"}
                            style={{
                              width: 28, height: 28, marginRight: 4, flexShrink: 0,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              background: projectsOpen ? C.surfaceHigh : "transparent",
                              border: `1px solid ${projectsOpen ? C.borderBright : "transparent"}`,
                              borderRadius: 8, color: projectsOpen ? C.blue : C.textMuted,
                              cursor: "pointer",
                            }}
                          >
                            {projectsOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                          </button>
                        )}
                      </div>
                      {!collapsed && projectsOpen && (
                        <div style={{
                          marginTop: 4, marginLeft: 6, paddingLeft: 8,
                          borderLeft: `1px solid ${C.border}`,
                          background: C.surface,
                          borderRadius: 10,
                          overflow: "hidden",
                        }}>
                          <InfrastructureExplorer embedded />
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <NavLink
                    key={item.id}
                    to={item.path}
                    end={item.path === "/"}
                    style={({ isActive }) => ({
                      display: "flex", alignItems: "center", gap: 10,
                      padding: collapsed ? "8px 0" : "7px 10px",
                      justifyContent: collapsed ? "center" : "flex-start",
                      borderRadius: 10,
                      color: isActive ? C.blue : C.textSub,
                      background: isActive ? C.blueDim : "transparent",
                      border: `1px solid ${isActive ? C.borderBright : "transparent"}`,
                      textDecoration: "none",
                      fontSize: 11, fontWeight: 500,
                      transition: "background 0.15s, color 0.15s",
                    })}
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon width={14} height={14} style={{ flexShrink: 0 }} />
                    {!collapsed && (
                      <>
                        <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.label}</span>
                        {item.badge && <Badge color={item.badgeColor}>{item.badge}</Badge>}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div style={{ padding: 10, borderTop: `1px solid ${C.border}`, display: "flex", flexDirection: "column", gap: 8 }}>
        {user && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: collapsed ? 0 : "4px 6px", justifyContent: collapsed ? "center" : "flex-start" }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: C.blueDim, border: `1px solid ${C.borderBright}`, color: C.blue, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
              {user.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
            </div>
            {!collapsed && (
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ color: C.text, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{user.name}</div>
                <div style={{ color: C.textMuted, fontSize: 9 }}>{user.role}</div>
              </div>
            )}
            {!collapsed && (
              <button onClick={logout} title="Logout" style={{ background: "transparent", border: "none", color: C.textMuted, cursor: "pointer", padding: 4 }}>
                <LogOut size={13} />
              </button>
            )}
          </div>
        )}
        <button
          onClick={onToggle}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 6, background: C.surface, color: C.textSub,
            border: `1px solid ${C.border}`, borderRadius: 8,
            cursor: "pointer",
          }}
        >
          {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
        </button>
      </div>
    </aside>
  );
}
